import { faExclamationTriangle } from "@fortawesome/pro-light-svg-icons";
import type { ProductUnitSystem, WeightUnit } from "@kanpla/api-contract";

import { T } from "@repo/transifex";
import { getUnitSystemLabel } from "~/utils/get-unit-system-label";
import { getScaleUnitLabel } from "~/utils/weight-units";

import { Modal, type ModalProps } from "../shared/modal";
import { ModalIcon } from "../shared/modal-icon";
import { ModalTitle } from "../shared/modal-title";
import { ModalButton } from "../shared/modal-button";
import { ModalButtonGroup } from "../shared/modal-button-group";

type ModalErrorScaleUnitProps = Pick<ModalProps, "open"> & {
  /** The unit the paired scale weighs in. */
  scaleUnit: WeightUnit;
  /** The unit system the product is priced per. */
  unitSystem: ProductUnitSystem | undefined;
  onClose: () => void;
};

/**
 * A reading from the other unit system is refused, never converted: the
 * server rejects it too, and a customer finding out at payment cannot tell why.
 */
export const ModalErrorScaleUnit = ({
  scaleUnit,
  unitSystem,
  onClose,
  open = true
}: ModalErrorScaleUnitProps) => {
  return (
    <Modal backdropVariant="error" open={open}>
      <div className="flex flex-col items-center gap-16 px-32 text-center">
        <ModalIcon icon={faExclamationTriangle} variant="error" />

        <ModalTitle size="9xl" variant="error" weight="black">
          <T _str="Wrong scale unit" />
        </ModalTitle>

        <p className="text-6xl text-kiosk-text-white">
          <T
            _str="This scale weighs in {scaleUnit}, but this product is priced {pricingLabel}. Check the scale setup."
            pricingLabel={getUnitSystemLabel(unitSystem)}
            scaleUnit={getScaleUnitLabel(scaleUnit)}
          />
        </p>

        <ModalButtonGroup className="mt-16">
          <ModalButton variant="error" onClick={onClose}>
            <T _str="Close" />
          </ModalButton>
        </ModalButtonGroup>
      </div>
    </Modal>
  );
};
